export * from 'core/shared/utils'

import { get as _get } from 'lodash'
import { GroupedItemsByProduct, ShippingParcelChecklist, TmrProduct as StwProduct } from 'stylewhere/api'

export const checkSelfUserAttribute = (code) => {
  return code === 'AS400username'
}

export const enabledUtilities = () => {
  return false
}

export const enabledDisassociateItemTag = () => {
  return true
}

export const getProductKeyAggregate = (enableBarcodeJde: boolean = true) => {
  return enableBarcodeJde ? 'attributes.barcodeJDE.value' : undefined // undefined a core
}

export const getEnableBarcodeJDE = (operation?: { attributes?: any }): boolean => {
  const operationAttributes = operation?.attributes as any
  return operationAttributes?.enableBarcodeJde && operationAttributes.enableBarcodeJde === 'true'

}

/**
 * This TypeScript function retrieves the barcode value of a product based on certain conditions,
 * prioritizing the JDE barcode if enabled.
 * @param product - The `getProductBarcodeValue` function takes two parameters:
 * @param [operation] - The `operation` parameter is an optional object that contains attributes
 * related to a specific operation. It is used in the `getProductBarcodeValue` function to determine
 * whether to use the JDE barcode or the BC barcode for a product. If `operation` is provided, the
 * function will check if the
 * @returns The `getProductBarcodeValue` function returns a string value, which could be either the
 * `barcodeJDE.value` if `useBarcodeJDE` is true and `barcodeJDE.value` is not empty, the
 * `product.attributes.barcode.value` if it is not empty, or the `product.code` if both
 * `barcodeJDE.value` and `product.attributes.barcode.value`
 */
export const getProductBarcodeValue = (
  product: { attributes?: any; code?: string },
  operation?: { attributes?: any }
): string => {
  if (!product) return ''

  const useBarcodeJDE = getEnableBarcodeJDE(operation)

  // Se enableBarcodeJde è true, usa barcodeJDE, altrimenti barcodeBC
  // Al momento enableBarcodeJde è true solo per la Cartonization USA
  if (useBarcodeJDE) {
    const barcodeJDE = product.attributes ? (product.attributes as any).barcodeJDE : undefined
    if (barcodeJDE && barcodeJDE.value && barcodeJDE.value !== '') {
      return barcodeJDE.value
    }
  }

  if (
    product.attributes &&
    product.attributes.barcode &&
    product.attributes.barcode.value &&
    product.attributes.barcode.value !== ''
  ) {
    return product.attributes.barcode.value
  }

  return product.code || ''
}

export const withFetchReplacedIdentifierOnTagInfo = true

/**
 * Corregge il detected per prodotti raggruppati per productKey (es. barcodeJDE).
 * Quando più prodotti distinti hanno lo stesso productKey, vengono raggruppati insieme,
 * ma il detected viene preso solo dal primo prodotto. Questa funzione somma i detected
 * di tutti i prodotti raggruppati.
 *
 * @param rows - I rows risultanti da OperationReadingProvider.groupItems
 * @param checklist - Il checklist contenente checklistProducts
 * @param products - L'oggetto products con i dettagli dei prodotti
 * @param productKey - La chiave usata per il raggruppamento (es. 'attributes.barcodeJDE.value')
 * @returns I rows con detected corretto
 */
export function fixDetectedForProductKey(
  rows: GroupedItemsByProduct[],
  checklist?: ShippingParcelChecklist | null,
  products?: { [upc: string]: StwProduct },
  productKey?: string
): GroupedItemsByProduct[] {
  // Se non si usa barcodeJDE, restituisci i rows senza modifiche
  if (!productKey || !productKey.includes('barcodeJDE')) {
    return rows
  }

  // Per ogni row, ricalcola detected basandosi sugli items effettivamente presenti
  return rows.map((row) => {
    if (!row.product || !row.items) {
      return row
    }

    // Conta gli items attualmente letti (non ancora confermati)
    const detectedFromItems = row.items.length

    // Se abbiamo il checklist, cerca il detected già confermato per questo prodotto
    let detectedFromChecklist = 0
    if (checklist && products) {
      const barcodeJDEValue = _get(row.product, productKey)
      if (barcodeJDEValue) {
        const checklistProducts = checklist.checklistProducts ?? checklist.products ?? []
        const matchingChecklistProducts = checklistProducts.filter((cp) => {
          const product = products[cp.productCode]
          if (!product) return false
          const productBarcodeJDE = _get(product, productKey)
          return productBarcodeJDE === barcodeJDEValue
        })
        
        // Somma i detected già confermati dal checklist
        detectedFromChecklist = matchingChecklistProducts.reduce((sum, cp) => {
          const detected = 'detected' in cp ? (cp.detected ?? 0) : 0
          return sum + detected
        }, 0)
      }
    }

    // Usa il maggiore tra items attuali e detected dal checklist
    // Questo gestisce sia il caso di items appena letti che items già confermati
const detected = Math.max(row.detected, detectedFromItems, detectedFromChecklist)

    return {
      ...row,
      detected
    }
  })
}
